// Per-engagement domain ownership management.
//
// Lists all assets, shows verification status, and exposes:
//   - Add asset (with inline typo-confirm label below the input)
//   - Edit value (allowed pre-verify and pre-scan; backend enforces)
//   - Verify ownership (DNS or HTTP file)
//   - Per-asset DNS + HTTP instructions with the token spelled out
//
// AI enrichment runs only on findings whose asset is ownership-verified
// (see app/ai/enrichment.py). This panel is the workflow that unlocks AI.

import { useMemo, useState } from "react";
import { Badge } from "@/components/ui/Badge";
import { Button } from "@/components/ui/Button";
import { Card } from "@/components/ui/Card";
import { KebabMenu, type KebabMenuItem } from "@/components/ui/KebabMenu";
import { confirm } from "@/stores/confirmStore";
import {
  useAddAsset,
  useChangeEngagementDomain,
  useEngagement,
  useEngagementAssets,
  useUpdateAsset,
  useVerifyAssetOwnership,
} from "@/hooks/useEngagementDetail";
import { ApiError } from "@/lib/api";
import type { Asset } from "@/types/engagement-detail";
import { useDict, fmt } from "@/i18n/LocaleProvider";

interface OwnershipPanelProps {
  engagementId: string;
}

export function OwnershipPanel({ engagementId }: OwnershipPanelProps) {
  const t = useDict().app.ownership;
  const { data: assets, isLoading } = useEngagementAssets(engagementId);
  const engagement = useEngagement(engagementId);
  const domainChangeUsed = !!engagement.data?.domain_changed_at;

  if (isLoading) {
    return (
      <Card>
        <div className="text-muted font-mono text-[11px]">{t.loadingAssets}</div>
      </Card>
    );
  }

  return (
    <div className="flex flex-col gap-3">
      <Card>
        <div className="font-mono text-[11px] text-muted uppercase tracking-[0.05em] mb-1">
          {t.domainOwnership}
        </div>
        <div className="font-sans text-[12px] text-content leading-[1.5]">
          {t.domainOwnershipDesc}
        </div>
      </Card>

      <AddAssetForm engagementId={engagementId} />

      {(assets ?? []).map((asset) => (
        <AssetCard
          key={asset.id}
          engagementId={engagementId}
          asset={asset}
          domainChangeUsed={domainChangeUsed}
        />
      ))}
    </div>
  );
}

// ─── add asset (with inline typo confirm) ────────────────────

function AddAssetForm({ engagementId }: { engagementId: string }) {
  const t = useDict().app.ownership;
  const [value, setValue] = useState("");
  const [error, setError] = useState<string | null>(null);
  const add = useAddAsset(engagementId);

  const trimmed = value.trim();
  const previewHost = useMemo(() => parseHost(trimmed), [trimmed]);

  async function submit() {
    if (!trimmed) return;
    setError(null);
    try {
      await add.mutateAsync({ asset_type: "domain", value: trimmed });
      setValue("");
    } catch (e) {
      setError(
        e instanceof ApiError
          ? String(e.detail)
          : e instanceof Error
            ? e.message
            : t.unknownError,
      );
    }
  }

  return (
    <Card>
      <div className="font-mono text-[10px] text-muted uppercase tracking-[0.08em] mb-2">
        {t.addDomain}
      </div>
      {/* Mirror Input's mobile sizing: text-base prevents iOS auto-zoom,
          py-3 + min-h-[44px] gives a real touch target. */}
      <div className="flex flex-col sm:flex-row gap-2">
        <input
          type="text"
          placeholder={t.addPlaceholder}
          value={value}
          onChange={(e) => setValue(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === "Enter") submit();
          }}
          className="flex-1 px-3 py-3 min-h-[44px] bg-bg2 border border-stroke rounded font-mono text-base md:text-[14px] text-content outline-none box-border"
        />
        <Button
          variant="primary"
          onClick={submit}
          disabled={!trimmed || add.isPending}
        >
          {add.isPending ? t.adding : t.add}
        </Button>
      </div>
      {previewHost && (
        <div className="mt-2 font-mono text-[11px] text-muted">
          {t.willScan} <span className="text-accent">{previewHost}</span>
        </div>
      )}
      {error && (
        <div className="mt-2 font-mono text-[11px] text-danger">
          {error}
        </div>
      )}
    </Card>
  );
}

// ─── per-asset card with verify + edit ───────────────────────

function AssetCard({
  engagementId,
  asset,
  domainChangeUsed,
}: {
  engagementId: string;
  asset: Asset;
  domainChangeUsed: boolean;
}) {
  const t = useDict().app.ownership;
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(asset.value);
  const [showInstructions, setShowInstructions] = useState(false);
  const [error, setError] = useState<string | null>(null);
  // Change-domain has its own input mode (separate from `editing` because
  // it hits a different backend endpoint and is destructive — wipes
  // findings/evidence and uses the engagement's one-shot domain change).
  const [changingDomain, setChangingDomain] = useState(false);
  const [changeDomainDraft, setChangeDomainDraft] = useState(asset.value);

  const update = useUpdateAsset(engagementId);
  const verify = useVerifyAssetOwnership(engagementId);
  const changeDomain = useChangeEngagementDomain(engagementId);
  const verified = asset.ownership_verified;

  async function saveEdit() {
    setError(null);
    try {
      await update.mutateAsync({ assetId: asset.id, value: draft.trim() });
      setEditing(false);
    } catch (e) {
      setError(
        e instanceof ApiError
          ? String(e.detail)
          : e instanceof Error
            ? e.message
            : t.unknownError,
      );
    }
  }

  async function runVerify() {
    setError(null);
    try {
      await verify.mutateAsync(asset.id);
    } catch (e) {
      setError(
        e instanceof ApiError
          ? String(e.detail)
          : e instanceof Error
            ? e.message
            : t.unknownError,
      );
    }
  }

  function startChangeDomain() {
    setError(null);
    setChangeDomainDraft(asset.value);
    setChangingDomain(true);
  }

  function cancelChangeDomain() {
    setChangingDomain(false);
    setChangeDomainDraft(asset.value);
  }

  async function submitChangeDomain() {
    setError(null);
    const newValue = changeDomainDraft.trim();
    if (!newValue || newValue === asset.value) {
      cancelChangeDomain();
      return;
    }
    // Destructive — confirm before firing the mutation. The on-brand
    // confirm modal replaces the legacy window.prompt+OK pattern (matches
    // the toast+confirm primitives shipped in PR #20).
    const ok = await confirm({
      title: t.changeTitle,
      message: fmt(t.changeMsg, { old: asset.value, new: newValue }),
      variant: "danger",
      confirmLabel: t.changeConfirm,
      cancelLabel: t.changeKeep,
    });
    if (!ok) return;
    try {
      await changeDomain.mutateAsync({ assetId: asset.id, newValue });
      setChangingDomain(false);
    } catch (e) {
      setError(
        e instanceof ApiError
          ? typeof e.detail === "string"
            ? e.detail
            : JSON.stringify(e.detail)
          : e instanceof Error
            ? e.message
            : t.unknownError,
      );
    }
  }

  return (
    <Card>
      <div className="flex items-center gap-3 mb-1">
        <div className="font-mono text-[13px] text-content flex-1 truncate">
          {editing ? (
            <input
              type="text"
              value={draft}
              onChange={(e) => setDraft(e.target.value)}
              className="w-full px-3 py-3 min-h-[44px] bg-bg2 border border-stroke rounded font-mono text-base md:text-[14px] text-content outline-none box-border"
            />
          ) : changingDomain ? (
            <input
              type="text"
              value={changeDomainDraft}
              onChange={(e) => setChangeDomainDraft(e.target.value)}
              autoFocus
              placeholder={t.newDomainPh}
              className="w-full px-3 py-3 min-h-[44px] bg-bg2 border border-danger/40 rounded font-mono text-base md:text-[14px] text-content outline-none box-border"
            />
          ) : (
            asset.value
          )}
        </div>
        {verified ? (
          <Badge color="rgb(var(--color-success))" bg="rgb(var(--color-success-bg))">{t.verified}</Badge>
        ) : (
          <Badge color="rgb(var(--color-warning))" bg="rgb(var(--color-warning-bg))">{t.unverified}</Badge>
        )}
      </div>

      {/* Hierarchy: only the primary action (Save / Verify / Apply) gets
          the accent-filled treatment so it stands out. Edit / Cancel /
          Show instructions stay on the bordered `default` style. Change
          domain — a destructive one-time action — uses an outline-only
          danger style: discoverable but not loud enough to compete. */}
      {editing ? (
        <div className="flex flex-wrap gap-3 mt-4">
          <Button variant="primary" onClick={saveEdit} disabled={update.isPending}>
            {t.save}
          </Button>
          <Button variant="default" onClick={() => { setEditing(false); setDraft(asset.value); }}>
            {t.cancel}
          </Button>
          {draft.trim() && draft.trim() !== asset.value && (
            <span className="font-mono text-[10px] text-muted self-center">
              {t.willScan} <span className="text-accent">{parseHost(draft.trim())}</span>
            </span>
          )}
        </div>
      ) : changingDomain ? (
        <>
          <div className="mt-2 font-mono text-[10px] text-danger leading-[1.5]">
            {t.destructiveWarn}
          </div>
          <div className="flex flex-wrap gap-3 mt-3">
            <Button
              variant="primary"
              onClick={submitChangeDomain}
              disabled={
                changeDomain.isPending ||
                !changeDomainDraft.trim() ||
                changeDomainDraft.trim() === asset.value
              }
            >
              {changeDomain.isPending ? t.changing : t.applyChange}
            </Button>
            <Button variant="default" onClick={cancelChangeDomain} disabled={changeDomain.isPending}>
              {t.cancel}
            </Button>
            {changeDomainDraft.trim() && changeDomainDraft.trim() !== asset.value && (
              <span className="font-mono text-[10px] text-muted self-center">
                {t.willScan} <span className="text-accent">{parseHost(changeDomainDraft.trim())}</span>
              </span>
            )}
          </div>
        </>
      ) : (
        // Idle state. Verify ownership stays as the loud primary action
        // when needed; everything else collapses into a kebab so the
        // row reads as "do the one important thing, with a quiet menu
        // of less-frequent actions next to it".
        <div className="flex flex-wrap items-center gap-3 mt-4">
          {!verified ? (
            <Button variant="primary" onClick={runVerify} disabled={verify.isPending}>
              {verify.isPending ? t.checking : t.verifyOwnership}
            </Button>
          ) : (
            <span className="font-mono text-[10px] text-muted">
              {t.readyToScan}
            </span>
          )}
          <KebabMenu
            ariaLabel={t.assetActions}
            items={
              [
                !verified && {
                  label: showInstructions
                    ? t.hideInstructions
                    : t.showInstructions,
                  onClick: () => setShowInstructions((s) => !s),
                },
                {
                  label: t.editAsset,
                  onClick: () => setEditing(true),
                },
                !domainChangeUsed && {
                  label: t.changeDomainMenu,
                  onClick: startChangeDomain,
                  variant: "danger" as const,
                },
              ].filter(Boolean) as KebabMenuItem[]
            }
          />
        </div>
      )}
      {domainChangeUsed && !editing && !changingDomain && (
        <div className="mt-2 font-mono text-[10px] text-muted">
          {t.domainChangeUsed}
        </div>
      )}

      {error && (
        <div className="mt-2 font-mono text-[11px] text-danger leading-[1.5]">
          {error}
        </div>
      )}

      {showInstructions && !verified && (
        <VerificationInstructions asset={asset} />
      )}
    </Card>
  );
}

function VerificationInstructions({ asset }: { asset: Asset }) {
  const t = useDict().app.ownership;
  const host = parseHost(asset.value);
  const dnsRecord = `_truehacking-verify.${host}`;
  const httpFile = `truehacking-verify-${asset.ownership_token}.txt`;
  const httpUrl = `https://${host}/.well-known/${httpFile}`;

  return (
    <div
      className="mt-3 p-3 rounded font-mono text-[11px]"
      style={{ background: "rgb(var(--color-bg))", border: "1px solid rgb(var(--color-stroke))" }}
    >
      <div className="text-accent uppercase text-[10px] tracking-[0.08em] mb-2">
        {t.method1}
      </div>
      <Kv label={t.recordName} value={dnsRecord} />
      <Kv label={t.recordType} value="TXT" />
      <Kv label={t.recordValue} value={asset.ownership_token} />

      <div className="text-accent uppercase text-[10px] tracking-[0.08em] mt-3 mb-2">
        {t.method2}
      </div>
      <Kv label={t.url} value={httpUrl} />
      <Kv label={t.fileContents} value={asset.ownership_token} />

      <div className="text-muted text-[10px] mt-3 leading-[1.5]">
        {t.instructionsHint}
      </div>
    </div>
  );
}

function Kv({ label, value }: { label: string; value: string }) {
  return (
    <div className="flex gap-2 mb-[3px]">
      <span className="text-muted w-[110px] flex-shrink-0">{label}:</span>
      <span className="text-content break-all">{value}</span>
    </div>
  );
}

// ─── helpers ────────────────────────────────────────────────

function parseHost(input: string): string {
  let v = input.trim().toLowerCase();
  if (!v) return "";
  if (v.includes("://")) {
    try {
      return new URL(v).hostname;
    } catch {
      return v;
    }
  }
  if (v.includes("/")) v = v.split("/", 1)[0];
  if (v.includes(":")) v = v.split(":", 1)[0];
  return v;
}
